<script lang="ts" setup>
import { ElNotification } from 'element-plus'
import { computed, onBeforeUnmount, onMounted, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import CampaignForm from '@/components/CampaignForm/CampaignForm.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { type CampaignFormData, serialiseCampaignForm } from '@/lib/helpers/campaign.helpers'
import type { Currency } from '@/types/store'
import type { UserTag } from '@/types/userTags'

const router = useRouter()
const route = useRoute()
const store = useStore()

const storeGroupId = computed<number>(() => store.getters['shop/storeGroupId'])
const state = computed(() => store.getters['campaign/state'])
const userTags = computed<UserTag[]>(() => store.getters['userTags/userTags'])
const currency = computed<Currency>(() => store.getters['shop/currency'])
const advertiserUsesCpa = computed(() => store.getters['shop/advertiserUsesCpa'])
const storeGroups = computed(() => {
  return store.getters['shop/storeGroups']?.map((item: { id: string }) => item.id)
})
const trafficSources = computed(() => store.getters['trafficSources/trafficSourcesTemplates'])
const isFetched = computed(() => {
  return userTags.value && state.value.data && trafficSources.value && state.value.isFetched
})
const handleBack = async () => await router.push('/campaigns')
const handleSubmit = async (data: CampaignFormData) => {
  const result = await store.dispatch('campaign/updateCampaign', {
    id: route.params.id,
    ...serialiseCampaignForm(data, storeGroups.value, storeGroupId.value, currency.value.code, true),
  })
  if (result) {
    await router.push('/campaigns')
    ElNotification.success('Your campaign has been updated')
  }
}

onMounted(() => store.dispatch('campaign/getCampaign', { id: route.params.id }))
onMounted(() => store.dispatch('userTags/initUserTags'))
onMounted(() => store.dispatch('trafficSources/fetchTrafficSources'))
onBeforeUnmount(async () => {
  await store.dispatch('campaign/reset')
})
watchEffect(() => {
  if (state.value.error && !isFetched.value) {
    void router.push({ name: 'error' })
  }
})
watchEffect(() => {
  if (state.value.error) {
    const isAccessError = [403, 404].includes(state.value.error.status)
    ElNotification.error({
      message: 'message' in state.value.error ? state.value.error.message : "Something went wrong and your campaign wasn't edited",
      duration: isAccessError ? 5000 : 0,
    })
    if (isAccessError) {
      void router.push('/campaigns')
    }
  }
})
</script>
<template>
  <div>
    <CampaignForm
      v-if="isFetched"
      :campaign="state.data"
      :is-loading="state.isLoading"
      :currency-symbol="currency?.symbol"
      :traffic-sources="trafficSources"
      :advertiser-uses-cpa="advertiserUsesCpa"
      :user-tags="userTags"
      @handle-back="handleBack"
      @handle-submit="handleSubmit"
    />
    <LoadingSpinner v-else />
  </div>
</template>
